/**
 * Robot (MuJoCo/mjcf) characters are hidden from every character panel in the product.
 *
 * They still exist: the S3 bundle and the Dynamo record are required for export and for
 * retargeting, which resolve a robot by id rather than by picking it from a list. Only the
 * *listings* exclude them, so nothing in the pipeline breaks while they are hidden.
 *
 * To show robots again, flip SHOW_ROBOT_CHARACTERS to true: the shared list helpers stop
 * filtering and the Motion Editor's Robots tab reappears.
 *
 * Cartographer is the one exception and stays robot-free either way, because its rig mapping
 * cannot handle an mjcf robot. That filter lives in Cartographer.tsx and is intentionally not
 * wired to this flag.
 */

/** The single source of truth for robot visibility. Set to true to bring robots back. */
export const SHOW_ROBOT_CHARACTERS = false;

/**
 * A robot is identified by its mjcf source upload. `extension` is the deliverable format and
 * `fileExtension` the uploaded one, and robots have been recorded under either, so both count.
 */
export function isRobotCharacter(character: {
  extension?: string | null;
  fileExtension?: string | null;
}): boolean {
  return character?.extension === 'mjcf' || character?.fileExtension === 'mjcf';
}

/**
 * Drop robots from a character listing, unless they are currently shown.
 *
 * `showRobots` defaults to the flag so callers stay a plain one-argument call; it is a parameter
 * so the behaviour can be tested in both states without the suite depending on which way the
 * flag currently points. Re-enabling robots stays a one-line change.
 */
export function withoutHiddenRobots<
  T extends { extension?: string | null; fileExtension?: string | null },
>(characters: T[], showRobots: boolean = SHOW_ROBOT_CHARACTERS): T[] {
  if (showRobots) return characters;
  return characters.filter((character) => !isRobotCharacter(character));
}
